import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Search,
  ChevronUp,
  ChevronDown,
  Database,
  FileText,
  BookOpen,
  Layers,
  GitBranch,
  Info,
  Share2,
  ListTree,
  MessageSquare,
  Network,
  FileJson,
} from 'lucide-react';
import {
  docProcessingApi,
  VerificationReport,
  IngestionHealthReport,
  HealthStatus,
} from '../../services/docProcessingApi';

type VerifySortField = 'status' | 'document' | 'query' | 'rank' | 'score';
type SortDir = 'asc' | 'desc';

const statusColors: Record<HealthStatus, { bg: string; border: string; text: string; icon: string }> = {
  healthy: { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700',  icon: 'text-green-500' },
  warning: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', icon: 'text-yellow-500' },
  error:   { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    icon: 'text-red-500' },
  info:    { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   icon: 'text-blue-500' },
};

function StatusIcon({ status }: { status: HealthStatus }) {
  const color = statusColors[status]?.icon || 'text-gray-400';
  if (status === 'healthy') return <CheckCircle size={16} className={color} />;
  if (status === 'warning') return <AlertTriangle size={16} className={color} />;
  if (status === 'error')   return <XCircle size={16} className={color} />;
  return <Info size={16} className={color} />;
}

function HealthCard({
  title,
  icon,
  status,
  metrics,
}: {
  title: string;
  icon: React.ReactNode;
  status: HealthStatus;
  metrics: { label: string; value: string | number }[];
}) {
  const colors = statusColors[status] || statusColors.info;
  return (
    <div className={`rounded-lg border p-4 ${colors.bg} ${colors.border}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-gray-600">{icon}</span>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>
        <StatusIcon status={status} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        {metrics.map((m) => (
          <div key={m.label}>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">{m.label}</p>
            <p className="text-sm font-medium text-gray-900">{m.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function IngestionHealth() {
  const [health, setHealth] = useState<IngestionHealthReport | null>(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [verification, setVerification] = useState<VerificationReport | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sort & filter for verification results
  const [verifySortField, setVerifySortField] = useState<VerifySortField>('status');
  const [verifySortDir, setVerifySortDir] = useState<SortDir>('asc');
  const [verifyFilterDoc, setVerifyFilterDoc] = useState('');
  const [verifyFilterQuery, setVerifyFilterQuery] = useState('');

  const loadHealth = useCallback(async () => {
    setLoadingHealth(true);
    try {
      const data = await docProcessingApi.getIngestionHealth();
      setHealth(data);
    } catch (err: any) {
      console.warn('Could not load ingestion health:', err.message);
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const runVerification = async () => {
    setVerifying(true);
    setError(null);
    try {
      const report = await docProcessingApi.verifyIngestion();
      setVerification(report);
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  // Sort toggle
  const toggleSort = (field: VerifySortField) => {
    if (verifySortField === field) {
      setVerifySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setVerifySortField(field);
      setVerifySortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: VerifySortField }) => {
    if (verifySortField !== field) return <ChevronUp size={10} className="text-gray-300" />;
    return verifySortDir === 'asc'
      ? <ChevronUp size={10} className="text-blue-600" />
      : <ChevronDown size={10} className="text-blue-600" />;
  };

  // Filtered + sorted verification results
  const sortedResults = useMemo(() => {
    if (!verification) return [];
    let list = Array.isArray(verification.results) ? [...verification.results] : [];

    if (verifyFilterDoc.trim()) {
      const q = verifyFilterDoc.toLowerCase();
      list = list.filter((r) => {
        const name = r.doc_title || r.source_path.split('/').pop() || '';
        return name.toLowerCase().includes(q);
      });
    }
    if (verifyFilterQuery.trim()) {
      const q = verifyFilterQuery.toLowerCase();
      list = list.filter((r) => r.query_used.toLowerCase().includes(q));
    }

    list.sort((a, b) => {
      let cmp = 0;
      switch (verifySortField) {
        case 'status': {
          const statusA = a.error ? 2 : a.found_in_results ? 0 : 1;
          const statusB = b.error ? 2 : b.found_in_results ? 0 : 1;
          cmp = statusA - statusB;
          break;
        }
        case 'document': {
          const nameA = a.doc_title || a.source_path.split('/').pop() || '';
          const nameB = b.doc_title || b.source_path.split('/').pop() || '';
          cmp = nameA.localeCompare(nameB);
          break;
        }
        case 'query':
          cmp = a.query_used.localeCompare(b.query_used);
          break;
        case 'rank':
          cmp = (a.rank ?? 999) - (b.rank ?? 999);
          break;
        case 'score':
          cmp = (a.score ?? -1) - (b.score ?? -1);
          break;
      }
      return verifySortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [verification, verifySortField, verifySortDir, verifyFilterDoc, verifyFilterQuery]);

  // Overall status summary
  const overallStatus = useMemo((): HealthStatus => {
    if (!health) return 'info';
    const statuses: HealthStatus[] = [
      health.embeddings.status,
      health.bm25_index.status,
      health.frontmatter.status,
      health.chunk_expansions.status,
      health.knowledge_graph.status,
      health.raptor_summaries.status,
      health.trained_qa?.status,
      health.schema_graph?.status,
      health.document_sources?.status,
    ].filter((s): s is HealthStatus => s != null);
    if (statuses.includes('error')) return 'error';
    if (statuses.includes('warning')) return 'warning';
    return 'healthy';
  }, [health]);

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <XCircle size={16} />
          </button>
        </div>
      )}

      {/* ── Pipeline Health Dashboard ─────────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Database size={18} />
              Pipeline Health
            </h2>
            <p className="text-xs text-gray-500 mt-0.5 ml-7">
              Coverage and status of all ingestion pipeline components.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {health && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[overallStatus].bg} ${statusColors[overallStatus].text}`}>
                {overallStatus === 'healthy' ? 'All Healthy' : overallStatus === 'warning' ? 'Needs Attention' : overallStatus === 'error' ? 'Issues Found' : 'Loading'}
              </span>
            )}
            <button
              onClick={loadHealth}
              disabled={loadingHealth}
              className="flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm transition-colors"
            >
              {loadingHealth ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Refresh
            </button>
          </div>
        </div>

        <div className="p-6">
          {loadingHealth && !health && (
            <div className="text-center py-8">
              <Loader2 size={32} className="text-blue-500 animate-spin mx-auto" />
              <p className="text-sm text-gray-600 mt-2">Loading pipeline health...</p>
            </div>
          )}

          {health && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Embeddings */}
              <HealthCard
                title="Embeddings (pgvector)"
                icon={<Database size={16} />}
                status={health.embeddings.status}
                metrics={[
                  { label: 'Coverage', value: `${health.embeddings.coverage_pct}%` },
                  { label: 'Embedded', value: `${health.embeddings.embedded_chunks} / ${health.embeddings.total_chunks}` },
                  { label: 'Dimension', value: `${health.embeddings.configured_dimension}-dim` },
                  { label: 'Zero Vectors', value: health.embeddings.zero_vectors_detected },
                ]}
              />

              {/* BM25 */}
              <HealthCard
                title="BM25 Index (Tantivy)"
                icon={<Search size={16} />}
                status={health.bm25_index.status}
                metrics={[
                  { label: 'Status', value: health.bm25_index.ready ? 'Ready' : 'Not Built' },
                  { label: 'Coverage', value: `${health.bm25_index.coverage_pct}%` },
                  { label: 'Indexed Chunks', value: health.bm25_index.indexed_chunks },
                  { label: 'Indexed Docs', value: health.bm25_index.indexed_documents },
                ]}
              />

              {/* Frontmatter */}
              <HealthCard
                title="Frontmatter"
                icon={<FileText size={16} />}
                status={health.frontmatter.status}
                metrics={[
                  { label: 'Coverage', value: `${health.frontmatter.coverage_pct}%` },
                  { label: 'Total Entries', value: health.frontmatter.total_entries },
                  { label: 'Missing', value: health.frontmatter.documents_missing_frontmatter },
                  { label: 'Incomplete', value: health.frontmatter.incomplete_entries },
                ]}
              />

              {/* Domain Terms */}
              <HealthCard
                title="Domain Terms"
                icon={<BookOpen size={16} />}
                status={health.domain_terms.status}
                metrics={[
                  { label: 'Terms', value: health.domain_terms.total_terms },
                  { label: 'Synonyms', value: health.domain_terms.total_synonyms },
                  { label: 'Cache Size', value: health.domain_terms.cache_terms },
                  { label: 'Status', value: health.domain_terms.total_terms > 0 ? 'Active' : 'Empty' },
                ]}
              />

              {/* Chunk Expansions */}
              <HealthCard
                title="Chunk Expansions"
                icon={<Layers size={16} />}
                status={health.chunk_expansions.status}
                metrics={[
                  { label: 'Coverage', value: `${health.chunk_expansions.coverage_pct}%` },
                  { label: 'Effectiveness', value: `${health.chunk_expansions.effectiveness_pct}%` },
                  { label: 'Effective', value: health.chunk_expansions.effective_expansions },
                  { label: 'Empty', value: health.chunk_expansions.empty_expansions },
                ]}
              />

              {/* Flow Diagrams */}
              <HealthCard
                title="Flow Diagrams"
                icon={<GitBranch size={16} />}
                status={health.flow_diagrams.status}
                metrics={[
                  { label: 'Diagrams', value: health.flow_diagrams.total_diagrams },
                  { label: 'Indexed Chunks', value: health.flow_diagrams.chunks_with_flow_summary },
                  { label: 'Missing Embed', value: health.flow_diagrams.flow_chunks_missing_embedding },
                  { label: '', value: '' },
                ]}
              />

              {/* Knowledge Graph */}
              <HealthCard
                title="Knowledge Graph"
                icon={<Share2 size={16} />}
                status={health.knowledge_graph.status}
                metrics={[
                  { label: 'Status', value: health.knowledge_graph.loaded ? 'Loaded' : 'Not Loaded' },
                  { label: 'Nodes', value: health.knowledge_graph.node_count },
                  { label: 'Edges', value: health.knowledge_graph.edge_count },
                  { label: 'Aliases', value: health.knowledge_graph.alias_count },
                ]}
              />

              {/* RAPTOR Summaries */}
              <HealthCard
                title="RAPTOR Summaries"
                icon={<ListTree size={16} />}
                status={health.raptor_summaries.status}
                metrics={[
                  { label: 'Claude API', value: health.raptor_summaries.llm_configured ? 'Configured' : 'Missing Key' },
                  { label: 'Model', value: health.raptor_summaries.model },
                  { label: 'Mode', value: 'On-the-fly' },
                  { label: 'Status', value: health.raptor_summaries.llm_configured ? 'Active' : 'Unavailable' },
                ]}
              />

              {/* Trained Q&A */}
              {health.trained_qa && (
                <HealthCard
                  title="Trained Q&A"
                  icon={<MessageSquare size={16} />}
                  status={health.trained_qa.status}
                  metrics={[
                    { label: 'Answers', value: `${health.trained_qa.active_answers} / ${health.trained_qa.total_answers}` },
                    { label: 'Variants', value: health.trained_qa.total_variants },
                    { label: 'Embed Coverage', value: `${health.trained_qa.embedding_coverage_pct}%` },
                    { label: 'Status', value: health.trained_qa.total_answers === 0 ? 'Empty' : health.trained_qa.embedding_coverage_pct === 100 ? 'Ready' : 'Partial' },
                  ]}
                />
              )}

              {/* Schema Graph / Neo4j */}
              {health.schema_graph && (
                <HealthCard
                  title="Schema Graph"
                  icon={<Network size={16} />}
                  status={health.schema_graph.status}
                  metrics={[
                    { label: 'Neo4j', value: health.schema_graph.neo4j_connected ? 'Connected' : 'Offline' },
                    { label: 'Schema', value: health.schema_graph.schema_graph_loaded ? 'Loaded' : 'Not Loaded' },
                    { label: 'Docs Linked', value: health.schema_graph.docs_linked },
                    { label: 'Total Links', value: health.schema_graph.total_links },
                  ]}
                />
              )}

              {/* Document Sources */}
              {health.document_sources && (
                <HealthCard
                  title="Document Sources"
                  icon={<FileJson size={16} />}
                  status={health.document_sources.status}
                  metrics={[
                    { label: 'Total Chunks', value: health.document_sources.total_chunks },
                    { label: 'Documents', value: health.document_sources.unique_documents },
                    { label: 'Doc Converted', value: health.document_sources.doc_converted_chunks },
                    { label: 'JSON Loaded', value: health.document_sources.json_loaded_chunks },
                  ]}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Post-Ingestion Verification (existing) ────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Search size={18} />
              Post-Ingestion Verification
            </h2>
            <p className="text-xs text-gray-500 mt-0.5 ml-7">
              Tests whether ingested documents are retrievable via vector similarity search (k-NN). Verifies each document's embedding returns itself in top-20 results.
            </p>
          </div>
          <button
            onClick={runVerification}
            disabled={verifying}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {verifying ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            {verifying ? 'Verifying...' : 'Run Verification'}
          </button>
        </div>

        <div className="p-6">
          {!verification && !verifying && (
            <div className="text-center text-gray-500 text-sm py-4">
              <Search size={32} className="text-gray-300 mx-auto mb-2" />
              <p>Click "Run Verification" to test whether all documents are retrievable via search.</p>
              <p className="text-xs text-gray-400 mt-1">
                For each document, a representative query is generated and tested against the search index.
              </p>
            </div>
          )}

          {verifying && (
            <div className="text-center py-8">
              <Loader2 size={32} className="text-blue-500 animate-spin mx-auto" />
              <p className="text-sm text-gray-600 mt-2">Running verification queries for each document...</p>
              <p className="text-xs text-gray-400 mt-1">This may take a minute.</p>
            </div>
          )}

          {verification && !verifying && (
            <div className="space-y-4">
              {/* Summary */}
              <div className={`rounded-lg p-4 border ${
                verification.passed
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center gap-3">
                  {verification.passed ? (
                    <CheckCircle size={24} className="text-green-600" />
                  ) : (
                    <XCircle size={24} className="text-red-600" />
                  )}
                  <div>
                    <p className={`font-semibold ${
                      verification.passed ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {verification.passed ? 'All Documents Verified' : 'Verification Failed'}
                    </p>
                    <p className={`text-sm ${
                      verification.passed ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {verification.found_count}/{verification.verified_count} documents found in search results
                      {verification.sampled && ` (sampled from ${verification.total_documents} total)`}
                      {verification.not_found_count > 0 && ` (${verification.not_found_count} missing)`}
                      {verification.error_count > 0 && ` (${verification.error_count} errors)`}
                    </p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-2xl font-bold text-gray-900">
                      {Math.round(verification.pass_rate * 100)}%
                    </p>
                    <p className="text-xs text-gray-500">
                      {verification.duration_ms.toFixed(0)}ms
                    </p>
                  </div>
                </div>
              </div>

              {/* Per-document results with sort + filter */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-500">
                      <th
                        className="px-4 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                        onClick={() => toggleSort('status')}
                      >
                        <span className="inline-flex items-center gap-1">Status <SortIcon field="status" /></span>
                      </th>
                      <th
                        className="px-4 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                        onClick={() => toggleSort('document')}
                      >
                        <span className="inline-flex items-center gap-1">Document <SortIcon field="document" /></span>
                      </th>
                      <th
                        className="px-4 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                        onClick={() => toggleSort('query')}
                      >
                        <span className="inline-flex items-center gap-1">Query Used <SortIcon field="query" /></span>
                      </th>
                      <th
                        className="px-4 py-2 font-medium text-right cursor-pointer hover:text-gray-900 select-none"
                        onClick={() => toggleSort('rank')}
                      >
                        <span className="inline-flex items-center gap-1">Rank <SortIcon field="rank" /></span>
                      </th>
                      <th
                        className="px-4 py-2 font-medium text-right cursor-pointer hover:text-gray-900 select-none"
                        onClick={() => toggleSort('score')}
                      >
                        <span className="inline-flex items-center gap-1">Score <SortIcon field="score" /></span>
                      </th>
                    </tr>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="px-4 py-1" />
                      <th className="px-4 py-1">
                        <input
                          type="text"
                          value={verifyFilterDoc}
                          onChange={(e) => setVerifyFilterDoc(e.target.value)}
                          placeholder="Filter..."
                          className="w-full text-[10px] border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 font-normal"
                        />
                      </th>
                      <th className="px-4 py-1">
                        <input
                          type="text"
                          value={verifyFilterQuery}
                          onChange={(e) => setVerifyFilterQuery(e.target.value)}
                          placeholder="Filter..."
                          className="w-full text-[10px] border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 font-normal"
                        />
                      </th>
                      <th className="px-4 py-1" />
                      <th className="px-4 py-1" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedResults.map((r, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2">
                          {r.error ? (
                            <AlertTriangle size={16} className="text-yellow-500" />
                          ) : r.found_in_results ? (
                            <CheckCircle size={16} className="text-green-500" />
                          ) : (
                            <XCircle size={16} className="text-red-500" />
                          )}
                        </td>
                        <td className="px-4 py-2 font-medium text-gray-900 max-w-xs truncate">
                          {r.doc_title || r.source_path.split('/').pop()}
                        </td>
                        <td className="px-4 py-2 text-gray-600 max-w-sm truncate" title={r.query_used}>
                          {r.query_used}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-600">
                          {r.rank ? `#${r.rank}` : '-'}
                        </td>
                        <td className="px-4 py-2 text-right text-gray-600">
                          {r.score != null ? r.score.toFixed(3) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
